import React, { useState, useEffect } from 'react';
import { X, Download, Mail, Eye, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import type { EventGroup } from '../types';

interface Props {
  group: EventGroup;
  onClose: () => void;
  onUpdate: () => void;
}

export default function GroupShareModal({ group, onClose, onUpdate }: Props) {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [copyCustomSuccess, setCopyCustomSuccess] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingTimingLogo, setUploadingTimingLogo] = useState(false);
  const [customSlug, setCustomSlug] = useState<string>(group.custom_url_slug || '');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [savingSlug, setSavingSlug] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const getShareUrl = (groupId: string) => {
    return `${window.location.origin}/results/group/${groupId}`;
  };

  const getCustomShareUrl = (slug: string) => {
    return `${window.location.origin}/results/group/${slug}`;
  };

  const copyToClipboard = async (text: string, isCustom = false) => {
    try {
      await navigator.clipboard.writeText(text);
      if (isCustom) {
        setCopyCustomSuccess('Copied!');
        setTimeout(() => setCopyCustomSuccess(null), 2000);
      } else {
        setCopySuccess('Copied!');
        setTimeout(() => setCopySuccess(null), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateSlug = (slug: string): boolean => {
    if (!slug) return true; // Empty is valid (removes custom slug)
    
    // Check length
    if (slug.length < 3 || slug.length > 50) {
      setSlugError('Custom URL must be between 3 and 50 characters');
      return false;
    }
    
    // Check format (alphanumeric and hyphens only)
    if (!/^[a-zA-Z0-9-]+$/.test(slug)) {
      setSlugError('Custom URL can only contain letters, numbers, and hyphens');
      return false;
    }
    
    setSlugError(null);
    return true;
  };

  const handleSaveCustomSlug = async () => {
    if (!validateSlug(customSlug)) return;
    
    setError(null);
    setSavingSlug(true);
    
    try {
      const slugToSave = customSlug.trim() || null;
      console.log("=== SAVE CUSTOM SLUG DEBUG ===");
      console.log("Group ID:", group.id);
      console.log("Slug to save:", slugToSave);
      console.log("Group user_id:", group.user_id);
      console.log("Current user ID from auth:", user?.id);
      
      // Check current user and group ownership
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      console.log("Current authenticated user:", currentUser?.id);
      console.log("User error:", userError);
      
      // Verify the group exists and we can read it
      const { data: existingGroup, error: readError } = await supabase
        .from('event_groups')
        .select('*')
        .eq('id', group.id)
        .maybeSingle();

      console.log("Existing group check:", { data: existingGroup, error: readError });

      if (readError) {
        console.error("Cannot read group:", readError);
        throw new Error(`Cannot access group: ${readError.message}`);
      }
      
      if (!existingGroup) {
        console.error("Group not found in database");
        throw new Error("Group not found in database");
      }
      
      console.log("Group ownership check:");
      console.log("- Group user_id:", existingGroup.user_id);
      console.log("- Current user ID:", currentUser?.id);
      console.log("- Match:", existingGroup.user_id === currentUser?.id);

      const { data: updatedData, error: updateError } = await supabase
        .from('event_groups')
        .update({ 
          custom_url_slug: slugToSave
        })
        .select('custom_url_slug')
        .eq('id', group.id);

      console.log("Update result - error:", updateError);
      console.log("Update result - data:", updatedData);
      
      if (updateError) {
        console.error("=== DATABASE UPDATE ERROR ===");
        console.error("Error code:", updateError.code);
        console.error("Error message:", updateError.message);
        console.error("Error details:", updateError.details);
        console.error("Error hint:", updateError.hint);
        throw updateError;
      }

      // Check if any rows were updated
      if (!updatedData || updatedData.length === 0) {
        console.error("=== NO ROWS UPDATED ===");
        console.error("This likely indicates an RLS policy is blocking the update");
        throw new Error("Unable to update group - permission denied or group not found");
      }

      console.log("=== SAVE SUCCESSFUL ===");
      console.log("Custom slug saved successfully to database:", updatedData[0].custom_url_slug);
      
      // Update the local group object immediately
      group.custom_url_slug = updatedData[0].custom_url_slug;
      console.log("Updated group object:", group);
      
      // Then update the parent component
      await onUpdate();
      setCopyCustomSuccess(null);
    } catch (err: any) {
      console.error("=== SAVE ERROR ===");
      console.error("Error type:", typeof err);
      console.error("Error object:", err);
      console.error("Error message:", err.message);
      console.error("Error code:", err.code);
      if (err.code === '23505') { // Unique constraint violation
        setSlugError('This custom URL is already taken. Please choose another.');
      } else {
        setError(`Failed to save custom URL: ${err.message}`);
      }
    } finally {
      setSavingSlug(false);
    }
  };

  const handleEventLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    setError(null);
    setUploadingLogo(true);

    try {
      if (group.shared_results_logo?.url) {
        const oldLogoPath = group.shared_results_logo.url.split('/').pop();
        if (oldLogoPath) {
          await supabase.storage
            .from('shared-logos')
            .remove([oldLogoPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${group.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          shared_results_logo: {
            url: publicUrl,
            height: 40
          }
        })
        .eq('id', group.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveEventLogo = async () => {
    if (!group.shared_results_logo?.url) return;

    setError(null);
    setUploadingLogo(true);

    try {
      const logoPath = group.shared_results_logo.url.split('/').pop();
      if (logoPath) {
        await supabase.storage
          .from('shared-logos')
          .remove([logoPath]);
      }

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          shared_results_logo: null
        })
        .eq('id', group.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUpdateEventLogoHeight = async (height: number) => {
    if (!group.shared_results_logo) return;

    try {
      const { error } = await supabase
        .from('event_groups')
        .update({
          shared_results_logo: {
            ...group.shared_results_logo,
            height
          }
        })
        .eq('id', group.id);

      if (error) throw error;
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleTimingLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be smaller than 2MB');
      return;
    }

    setError(null);
    setUploadingTimingLogo(true);

    try {
      if (group.timing_company_logo?.url) {
        const oldLogoPath = group.timing_company_logo.url.split('/').pop();
        if (oldLogoPath) {
          await supabase.storage
            .from('shared-logos')
            .remove([oldLogoPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `timing-${group.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-logos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-logos')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          timing_company_logo: {
            url: publicUrl,
            name: group.timing_company_logo?.name || ''
          }
        })
        .eq('id', group.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingTimingLogo(false);
    }
  };

  const handleRemoveTimingLogo = async () => {
    if (!group.timing_company_logo?.url) return;

    setError(null);
    setUploadingTimingLogo(true);

    try {
      const logoPath = group.timing_company_logo.url.split('/').pop();
      if (logoPath) {
        await supabase.storage
          .from('shared-logos')
          .remove([logoPath]);
      }

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          timing_company_logo: null
        })
        .eq('id', group.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingTimingLogo(false);
    }
  };

  const handleBackgroundUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Background image must be smaller than 5MB');
      return;
    }

    setError(null);
    setUploadingBackground(true);

    try {
      if (group.shared_results_background?.url) {
        const oldBgPath = group.shared_results_background.url.split('/').pop();
        if (oldBgPath) {
          await supabase.storage
            .from('shared-backgrounds')
            .remove([oldBgPath]);
        }
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${group.id}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('shared-backgrounds')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('shared-backgrounds')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          shared_results_background: {
            url: publicUrl,
            opacity: 0.15
          }
        })
        .eq('id', group.id);

      if (updateError) throw updateError;
      
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!group.shared_results_background?.url) return;

    setError(null);
    setUploadingBackground(true);

    try {
      const bgPath = group.shared_results_background.url.split('/').pop();
      if (bgPath) {
        await supabase.storage
          .from('shared-backgrounds')
          .remove([bgPath]);
      }

      const { error: updateError } = await supabase
        .from('event_groups')
        .update({
          shared_results_background: null
        })
        .eq('id', group.id);

      if (updateError) throw updateError;

      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploadingBackground(false);
    }
  };

  const handleUpdateBackgroundOpacity = async (opacity: number) => {
    if (!group.shared_results_background) return;

    try {
      const { error } = await supabase
        .from('event_groups')
        .update({
          shared_results_background: {
            ...group.shared_results_background,
            opacity
          }
        })
        .eq('id', group.id);

      if (error) throw error;
      await onUpdate();
      setPreviewKey(prev => prev + 1);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-[calc(100vh-2rem)] flex items-center justify-center w-full">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl w-full max-w-4xl my-4 border border-white/50">
          <div className="flex justify-between items-center p-6 border-b border-gray-200/50">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Share Multi Race Results</h2>
              <p className="text-gray-600 mt-1">Configure how your results page appears to participants</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-8 max-h-[calc(100vh-16rem)] overflow-y-auto">
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2 border border-red-200">
                <X className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Share URL Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Share URL</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Results Page URL
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={getShareUrl(group.id)}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white rounded-md border border-gray-300 focus:ring-gray-500 focus:border-gray-500 text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(getShareUrl(group.id))}
                    className={`px-4 py-2 ${
                      copySuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'
                    } text-white rounded-md text-sm min-w-[100px] whitespace-nowrap transition-colors`}
                  >
                    {copySuccess || 'Copy'}
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Share this link to allow anyone to view the results for this multi race.
                </p>
              </div>
            </section>

            {/* Custom URL Section */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Custom URL</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {group.custom_url_slug ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Custom URL
                      </label>
                      <button
                        onClick={() => setShowRemoveConfirm(true)}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        value={getCustomShareUrl(group.custom_url_slug)}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white rounded-md border border-gray-300 focus:ring-gray-500 focus:border-gray-500 text-sm font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(getCustomShareUrl(group.custom_url_slug), true)}
                        className={`px-4 py-2 ${
                          copyCustomSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'
                        } text-white rounded-md text-sm min-w-[100px] whitespace-nowrap transition-colors`}
                      >
                        {copyCustomSuccess || 'Copy'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Create a custom, memorable URL for sharing your multi race results
                    </label>
                    <div className="flex items-center mb-2">
                      <input
                        type="text"
                        value={customSlug}
                        onChange={(e) => {
                          setCustomSlug(e.target.value);
                          validateSlug(e.target.value);
                        }}
                        placeholder="my-multi-race"
                        className={`flex-1 px-3 py-2 bg-white rounded-md border ${
                          slugError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-gray-500 focus:border-gray-500'
                        } text-sm`}
                      />
                    </div>
                    
                    {customSlug && !slugError && (
                      <div className="mb-4 text-sm text-gray-600 break-all">
                        <span className="font-medium">Preview: </span>{getCustomShareUrl(customSlug)}
                      </div>
                    )}
                    
                    {slugError && (
                      <div className="mb-4 text-sm text-red-500">
                        {slugError}
                      </div>
                    )}
                    
                    <button
                      onClick={handleSaveCustomSlug}
                      disabled={savingSlug || !!slugError || !customSlug.trim()}
                      className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {savingSlug ? 'Saving...' : 'Save Custom URL'}
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* Visual Customization Section */}
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Visual Customization</h3>
              <p className="text-gray-600 mb-6">Customize the appearance of your shared results page</p>
              
              <div className="space-y-8">
                {/* Event Logo Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Event Logo
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {group.shared_results_logo?.url ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-32 bg-white rounded-md overflow-hidden border border-gray-200">
                            <img
                              src={group.shared_results_logo.url}
                              alt="Event Logo"
                              className="h-full w-full object-contain"
                            />
                          </div>
                          <button
                            onClick={handleRemoveEventLogo}
                            disabled={uploadingLogo}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                            title="Remove logo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo Height
                          </label>
                          <div className="flex items-center gap-4">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <input
                                type="range"
                                min="20"
                                max="80"
                                value={group.shared_results_logo.height || 40}
                                onChange={(e) => handleUpdateEventLogoHeight(parseInt(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                              />
                            </div>
                            <div className="w-16 text-sm text-gray-600 tabular-nums text-right">
                              {group.shared_results_logo.height || 40}px
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Small</span>
                            <span>Large</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEventLogoUpload}
                          disabled={uploadingLogo}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-blue-50 file:text-blue-700
                            hover:file:bg-blue-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload your event logo. Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timing Company Logo Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    Timing Company Logo
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {group.timing_company_logo?.url ? (
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-32 bg-white rounded-md overflow-hidden border border-gray-200">
                          <img
                            src={group.timing_company_logo.url}
                            alt="Timing Company Logo"
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <button
                          onClick={handleRemoveTimingLogo}
                          disabled={uploadingTimingLogo}
                          className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                          title="Remove timing logo"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleTimingLogoUpload}
                          disabled={uploadingTimingLogo}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-purple-50 file:text-purple-700
                            hover:file:bg-purple-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload your timing company logo. Maximum file size: 2MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Background Image Subsection */}
                <div>
                  <h4 className="text-base font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Background Image
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    {group.shared_results_background?.url ? (
                      <div className="space-y-4">
                        <div className="flex items-start gap-4">
                          <div className="h-32 flex-1 bg-white rounded-md overflow-hidden border border-gray-200">
                            <img
                              src={group.shared_results_background.url}
                              alt="Background Preview"
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <button
                            onClick={handleRemoveBackground}
                            disabled={uploadingBackground}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50 p-2 rounded-full hover:bg-red-50 transition-colors"
                            title="Remove background"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Background Opacity
                          </label>
                          <div className="flex items-center gap-4">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <div className="flex-1">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={Math.round((group.shared_results_background.opacity || 0) * 100)}
                                onChange={(e) => handleUpdateBackgroundOpacity(parseInt(e.target.value) / 100)}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                              />
                            </div>
                            <div className="w-16 text-sm text-gray-600 tabular-nums text-right">
                              {Math.round((group.shared_results_background.opacity || 0) * 100)}%
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Transparent</span>
                            <span>Solid</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBackgroundUpload}
                          disabled={uploadingBackground}
                          className="w-full px-3 py-2 text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-semibold
                            file:bg-emerald-50 file:text-emerald-700
                            hover:file:bg-emerald-100 disabled:opacity-50"
                        />
                        <p className="mt-2 text-sm text-gray-600">
                          Upload a background image for your results page. Maximum file size: 5MB. Supported formats: PNG, JPG, GIF
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Preview Section */}
          </div>
        </div>
      </div>
      
      {/* Custom Remove Confirmation Dialog */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60] backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-xl w-full max-w-md p-6 border border-white/50">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Remove Custom URL</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this custom URL? This will make your current custom link inaccessible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRemoveConfirm(false);
                  setError(null);
                  setSavingSlug(true);
                  
                  supabase
                    .from('event_groups')
                    .update({
                      custom_url_slug: null
                    })
                    .eq('id', group.id)
                    .then(({ error: updateError }) => {
                      if (updateError) {
                        setError(updateError.message);
                      } else {
                        setCustomSlug('');
                        // Update the local group object immediately
                        group.custom_url_slug = null;
                        console.log("Removed custom URL from group object:", group);
                        onUpdate();
                      }
                    })
                    .finally(() => {
                      setSavingSlug(false);
                    });
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
              >
                Remove URL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}